/**
 * Main App Component
 * Sets up routing, providers, and theme
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { PrivateRoute } from './components/Auth/PrivateRoute';
import { Login } from './components/Auth/Login';
import { AppLayout } from './components/Layout/AppLayout';
import { GameList } from './components/Games/GameList';
import { GameDetail } from './components/Games/GameDetail';
import { GameEditor } from './components/Games/GameEditor';
import { SubmissionReview } from './components/Submissions';
import { TranslationTool, CopyTool } from './components/Tools';
import { ContributorReview } from './components/Contributors';
import { Dashboard } from './components/Dashboard';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  }
});

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2'
    },
    secondary: {
      main: '#dc004e'
    }
  }
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Login route - public */}
              <Route path="/login" element={<Login />} />

              {/* Dashboard - protected (home page) */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <AppLayout>
                      <Dashboard />
                    </AppLayout>
                  </PrivateRoute>
                }
              />

              {/* Game list - protected */}
              <Route
                path="/games"
                element={
                  <PrivateRoute>
                    <AppLayout>
                      <GameList />
                    </AppLayout>
                  </PrivateRoute>
                }
              />

              {/* Game editor - new game (must come before :id route) */}
              <Route
                path="/games/new"
                element={
                  <PrivateRoute>
                    <AppLayout>
                      <GameEditor />
                    </AppLayout>
                  </PrivateRoute>
                }
              />

              {/* Game editor - edit existing */}
              <Route
                path="/games/:id/edit"
                element={
                  <PrivateRoute>
                    <AppLayout>
                      <GameEditor />
                    </AppLayout>
                  </PrivateRoute>
                }
              />

              {/* Game detail - protected */}
              <Route
                path="/games/:id"
                element={
                  <PrivateRoute>
                    <AppLayout>
                      <GameDetail />
                    </AppLayout>
                  </PrivateRoute>
                }
              />

              {/* Submissions review - protected */}
              <Route
                path="/submissions"
                element={
                  <PrivateRoute>
                    <AppLayout>
                      <SubmissionReview />
                    </AppLayout>
                  </PrivateRoute>
                }
              />

              {/* Translation tool - protected */}
              <Route
                path="/tools/translate"
                element={
                  <PrivateRoute>
                    <AppLayout>
                      <TranslationTool />
                    </AppLayout>
                  </PrivateRoute>
                }
              />

              {/* Copy tool - protected */}
              <Route
                path="/tools/copy"
                element={
                  <PrivateRoute>
                    <AppLayout>
                      <CopyTool />
                    </AppLayout>
                  </PrivateRoute>
                }
              />

              {/* Contributors management - protected */}
              <Route
                path="/contributors"
                element={
                  <PrivateRoute>
                    <AppLayout>
                      <ContributorReview />
                    </AppLayout>
                  </PrivateRoute>
                }
              />

              {/* Redirect any unknown routes to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
